import React, { useEffect, useRef, useState } from "react";
import { Drawer, Form, Input, Switch, Row, Col, message, Button, Space, Typography, Spin } from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { useCreate, useUpdate } from "../../../../hooks";
import { useAuth } from "../../../../hooks/useAuth";
import { contractManagementService } from "../../../../api/enhancedApi";
import { BUSINESS_PARTNER_PERMISSIONS, DRAWER_MODES } from "../constants";

const { TextArea } = Input;
const { Title } = Typography;

const TEXT_FIELDS = ["name", "name_cn", "code", "contact_person", "contact_email", "contact_phone", "address", "business_license", "tax_number", "remarks"];

const DRAWER_TITLES = {
    [DRAWER_MODES.ADD]: "新增客户",
    [DRAWER_MODES.EDIT]: "编辑客户",
    [DRAWER_MODES.VIEW]: "查看客户",
};

const normalizeText = (value) => {
    if (value === undefined || value === null) return null;
    const text = String(value).trim();
    return text || null;
};

const toFormValues = (record) => {
    const values = { is_active: Boolean(record?.is_active) };
    TEXT_FIELDS.forEach((field) => {
        values[field] = record?.[field] ?? undefined;
    });
    return values;
};

const buildCreatePayload = (values) => {
    const payload = {};
    TEXT_FIELDS.forEach((field) => {
        const text = normalizeText(values[field]);
        if (text !== null) payload[field] = text;
    });
    return payload;
};

// 编辑时清空的字段必须显式提交 null，否则后端视为"未修改"，旧值会留在库里。
const buildUpdatePayload = (values, original) => {
    const payload = {};
    TEXT_FIELDS.forEach((field) => {
        payload[field] = normalizeText(values[field]);
    });
    if (!payload.code) {
        if (normalizeText(original?.code)) {
            return { error: "已有编码不能清空" };
        }
        delete payload.code;
    }
    payload.is_active = Boolean(values.is_active);
    return { payload };
};

const showValidationErrors = (error) => {
    const validationErrors = error?.response?.status === 422 ? error.response.data?.validation_errors : null;
    if (!Array.isArray(validationErrors)) return;
    validationErrors.forEach((err, index) => {
        setTimeout(() => {
            message.error(`字段 ${err.field}: ${err.message}`, 5);
        }, index * 500);
    });
};

const BusinessPartnerDrawer = ({ visible, mode, initialValues, onSuccess, onCancel, title }) => {
    const [form] = Form.useForm();
    const { hasPermission } = useAuth();
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const detailRequestSeqRef = useRef(0);

    const isView = mode === DRAWER_MODES.VIEW;
    const isEdit = mode === DRAWER_MODES.EDIT;
    const isAdd = !isView && !isEdit;

    const createMutation = useCreate("客户", contractManagementService.businessPartner.createBusinessPartner, {
        onSuccess: () => onSuccess(),
        onError: showValidationErrors,
    });

    const updateMutation = useUpdate("客户", contractManagementService.businessPartner.updateBusinessPartner, {
        onSuccess: () => onSuccess(),
        onError: showValidationErrors,
    });

    const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

    useEffect(() => {
        // 每次打开/切换对象都换一个序号，迟到的旧详情响应直接丢弃，避免把 A 的内容填进 B 的表单。
        const requestSeq = ++detailRequestSeqRef.current;

        if (!visible) {
            setDetailData(null);
            setIsLoadingDetail(false);
            return;
        }

        if (isAdd || !initialValues?.id) {
            form.resetFields();
            form.setFieldsValue({ is_active: true });
            setDetailData(null);
            return;
        }

        form.setFieldsValue(toFormValues(initialValues));
        setIsLoadingDetail(true);
        contractManagementService.businessPartner
            .getBusinessPartnerById(initialValues.id)
            .then((response) => {
                if (requestSeq !== detailRequestSeqRef.current) return;
                if (!response?.data) {
                    throw new Error("获取客户详情失败：服务器返回无效数据");
                }
                setDetailData(response.data);
                form.setFieldsValue(toFormValues(response.data));
            })
            .catch((error) => {
                if (requestSeq !== detailRequestSeqRef.current) return;
                console.error("获取客户详情失败:", error);
                const errorMsg = error.response?.data?.detail || error.message || "获取客户详情失败";
                message.error(`${errorMsg}，当前显示的是列表中的数据`);
            })
            .finally(() => {
                if (requestSeq === detailRequestSeqRef.current) {
                    setIsLoadingDetail(false);
                }
            });
    }, [visible, isAdd, initialValues, form]);

    const original = detailData || initialValues;
    const originalActive = Boolean(original?.is_active);
    const canChangeStatus = originalActive ? hasPermission(BUSINESS_PARTNER_PERMISSIONS.DEACTIVATE) : hasPermission(BUSINESS_PARTNER_PERMISSIONS.ACTIVATE);

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (error) {
            return;
        }

        if (isAdd) {
            createMutation.mutate(buildCreatePayload(values));
            return;
        }

        if (!initialValues?.id) {
            message.error("编辑数据异常：缺少客户ID，请刷新页面重试");
            return;
        }
        const { payload, error } = buildUpdatePayload(values, original);
        if (error) {
            message.error(error);
            return;
        }
        updateMutation.mutate({ id: initialValues.id, data: payload });
    };

    const handleCancel = () => {
        form.resetFields();
        setDetailData(null);
        onCancel();
    };

    const drawerTitle = title || DRAWER_TITLES[mode] || DRAWER_TITLES[DRAWER_MODES.ADD];

    return (
        <Drawer
            title={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Title level={4} style={{ margin: 0 }}>
                        {drawerTitle}
                        {!isAdd && initialValues?.name && <span style={{ fontSize: "14px", color: "#666", fontWeight: "normal", marginLeft: "8px" }}>- {initialValues.name}</span>}
                    </Title>
                </div>
            }
            placement="right"
            open={visible}
            onClose={handleCancel}
            width={600}
            destroyOnHidden
            maskClosable={isView}
            extra={
                <Space>
                    <Button icon={<CloseOutlined />} onClick={handleCancel}>
                        {isView ? "关闭" : "取消"}
                    </Button>
                    {!isView && (
                        <Button type="primary" icon={<SaveOutlined />} onClick={handleSubmit} loading={isSubmitting} disabled={isLoadingDetail}>
                            保存
                        </Button>
                    )}
                </Space>
            }
        >
            {isLoadingDetail && (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16, color: "#666" }}>正在加载详细信息...</p>
                </div>
            )}

            <Form form={form} layout="vertical" preserve={false} size="default" disabled={isView || isLoadingDetail} style={{ opacity: isLoadingDetail ? 0.6 : 1 }}>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="name"
                            label="英文名称"
                            rules={[
                                { required: true, whitespace: true, message: "请输入英文名称" },
                                { max: 200, message: "英文名称不能超过200个字符" },
                            ]}
                            extra={isView ? undefined : "同一组织内英文名称不能重复"}
                        >
                            <Input placeholder="请输入英文名称" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item name="name_cn" label="中文名称" rules={[{ max: 200, message: "中文名称不能超过200个字符" }]}>
                            <Input placeholder="请输入中文名称（选填）" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item name="code" label="客户编码" rules={[{ max: 50, message: "客户编码不能超过50个字符" }]}>
                            <Input placeholder="可留空，保存时由系统生成" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="contact_person" label="联系人" rules={[{ max: 100, message: "联系人姓名不能超过100个字符" }]}>
                            <Input placeholder="请输入联系人姓名" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="contact_phone"
                            label="联系电话"
                            rules={[
                                { max: 50, message: "联系电话不能超过50个字符" },
                                { pattern: /^[\d\-+()\s]+$/, message: "请输入有效的电话号码" },
                            ]}
                        >
                            <Input placeholder="请输入联系电话" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="contact_email"
                            label="联系邮箱"
                            rules={[
                                { type: "email", message: "请输入有效的邮箱地址" },
                                { max: 200, message: "邮箱地址不能超过200个字符" },
                            ]}
                        >
                            <Input placeholder="请输入联系邮箱" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item name="address" label="地址" rules={[{ max: 500, message: "地址不能超过500个字符" }]}>
                            <TextArea rows={2} placeholder="请输入地址" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="business_license" label="营业执照号" rules={[{ max: 100, message: "营业执照号不能超过100个字符" }]}>
                            <Input placeholder="请输入营业执照号" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="tax_number" label="税号" rules={[{ max: 100, message: "税号不能超过100个字符" }]}>
                            <Input placeholder="请输入税号" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item name="remarks" label="备注" rules={[{ max: 1000, message: "备注不能超过1000个字符" }]}>
                            <TextArea rows={3} placeholder="请输入备注信息" />
                        </Form.Item>
                    </Col>
                </Row>

                {!isAdd && (
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="is_active" label="状态" valuePropName="checked" extra={isEdit && !canChangeStatus ? `没有${originalActive ? "停用" : "启用"}客户的权限，状态不可修改` : undefined}>
                                <Switch checkedChildren="启用" unCheckedChildren="停用" disabled={isEdit && !canChangeStatus} style={{ marginTop: 4 }} />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
            </Form>
        </Drawer>
    );
};

export default BusinessPartnerDrawer;
